import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Progress } from '../components/ui/progress';
import { 
  AlertTriangle, TrendingUp, TrendingDown, Minus, Shield, 
  Users, ChevronRight, RefreshCcw, Info, Eye
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Agent Risk Scoring (ARS) Admin Panel
 * Phase 1: Shadow Mode - Internal Admin Only
 * 
 * Displays agent risk scores, tier distribution, and detailed breakdowns
 */
const ARSAdminPanel = ({ token }) => {
  const [overview, setOverview] = useState(null);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [agentDetails, setAgentDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchOverview();
  }, [token]);

  const fetchOverview = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/ars/overview`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        setOverview(data.data);
      } else {
        setError('Failed to load ARS overview');
      }
    } catch (err) {
      setError('Error connecting to ARS service');
    }
    setLoading(false);
  };

  const fetchAgentDetails = async (agentId) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/ars/agents/${agentId}/risk-score`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        setAgentDetails(data.data);
        setSelectedAgent(agentId);
      }
    } catch (err) {
      console.error('Error fetching agent details:', err);
    }
  };

  const getTierColor = (tier) => {
    switch (tier) {
      case 'TIER_A': return 'bg-green-600/20 text-green-400 border-green-600/30';
      case 'TIER_B': return 'bg-blue-600/20 text-blue-400 border-blue-600/30';
      case 'TIER_C': return 'bg-yellow-600/20 text-yellow-400 border-yellow-600/30';
      case 'RESTRICTED': return 'bg-red-600/20 text-red-400 border-red-600/30';
      default: return 'bg-gray-600/20 text-gray-400 border-gray-600/30';
    }
  };

  const getTierLabel = (tier) => {
    switch (tier) {
      case 'TIER_A': return 'Priority';
      case 'TIER_B': return 'Standard';
      case 'TIER_C': return 'Watchlist';
      case 'RESTRICTED': return 'Restricted';
      default: return 'Unscored';
    }
  };

  const getTrendIcon = (direction) => {
    switch (direction) {
      case 'UP': return <TrendingUp className="w-4 h-4 text-green-400" />;
      case 'DOWN': return <TrendingDown className="w-4 h-4 text-red-400" />;
      default: return <Minus className="w-4 h-4 text-gray-400" />;
    }
  };

  const getConfidenceColor = (confidence) => {
    switch (confidence) {
      case 'HIGH': return 'text-green-400';
      case 'MEDIUM': return 'text-yellow-400';
      case 'LOW': return 'text-orange-400';
      default: return 'text-gray-400';
    }
  };

  if (loading) {
    return (
      <Card className="glass-card border-white/10">
        <CardContent className="p-8 text-center">
          <RefreshCcw className="w-8 h-8 animate-spin mx-auto mb-4 text-primary" />
          <p className="text-muted-foreground">Loading Agent Risk Scores...</p>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="glass-card border-red-600/30">
        <CardContent className="p-8 text-center">
          <AlertTriangle className="w-8 h-8 mx-auto mb-4 text-red-400" />
          <p className="text-red-400">{error}</p>
          <Button onClick={fetchOverview} className="mt-4">Retry</Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-display text-primary">Agent Risk Scoring</h2>
          <p className="text-sm text-muted-foreground">Phase 1: Shadow Mode - Internal Only</p>
        </div>
        <Button variant="outline" onClick={fetchOverview} className="gap-2">
          <RefreshCcw className="w-4 h-4" /> Refresh
        </Button>
      </div>

      {/* Phase Banner */}
      <Card className="glass-card border-blue-600/30 bg-blue-600/5">
        <CardContent className="p-4 flex items-center gap-4">
          <Info className="w-5 h-5 text-blue-400" />
          <div>
            <p className="text-blue-400 font-medium">Shadow Mode Active</p>
            <p className="text-xs text-muted-foreground">
              Scores are calculated but not enforced. No restrictions applied. Predictions being validated.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Tier Distribution */}
      <div className="grid grid-cols-5 gap-4">
        <Card className="glass-card border-green-600/30">
          <CardContent className="p-4 text-center">
            <div className="text-3xl font-display text-green-400">{overview?.tier_a_count || 0}</div>
            <p className="text-xs text-muted-foreground">Tier A</p>
            <p className="text-xs text-green-400">Priority</p>
          </CardContent>
        </Card>
        <Card className="glass-card border-blue-600/30">
          <CardContent className="p-4 text-center">
            <div className="text-3xl font-display text-blue-400">{overview?.tier_b_count || 0}</div>
            <p className="text-xs text-muted-foreground">Tier B</p>
            <p className="text-xs text-blue-400">Standard</p>
          </CardContent>
        </Card>
        <Card className="glass-card border-yellow-600/30">
          <CardContent className="p-4 text-center">
            <div className="text-3xl font-display text-yellow-400">{overview?.tier_c_count || 0}</div>
            <p className="text-xs text-muted-foreground">Tier C</p>
            <p className="text-xs text-yellow-400">Watchlist</p>
          </CardContent>
        </Card>
        <Card className="glass-card border-red-600/30">
          <CardContent className="p-4 text-center">
            <div className="text-3xl font-display text-red-400">{overview?.restricted_count || 0}</div>
            <p className="text-xs text-muted-foreground">Restricted</p>
            <p className="text-xs text-red-400">{"<50"}</p>
          </CardContent>
        </Card>
        <Card className="glass-card border-gray-600/30">
          <CardContent className="p-4 text-center">
            <div className="text-3xl font-display text-gray-400">{overview?.unscored_count || 0}</div>
            <p className="text-xs text-muted-foreground">Unscored</p>
            <p className="text-xs text-gray-400">No Data</p>
          </CardContent>
        </Card>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-3 gap-6">
        {/* Agent List */}
        <Card className="glass-card border-white/10 col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-primary" />
              Agent Scores
              <Badge variant="secondary" className="ml-auto">{overview?.total_agents || 0} Agents</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-[500px] overflow-y-auto">
              {overview?.agents?.map((agent) => (
                <div
                  key={agent.agent_id}
                  onClick={() => fetchAgentDetails(agent.agent_id)}
                  className={`p-4 rounded-lg border cursor-pointer transition-all ${
                    selectedAgent === agent.agent_id 
                      ? 'border-primary bg-primary/10' 
                      : 'border-white/10 hover:border-white/20'
                  }`}
                  data-testid={`agent-row-${agent.agent_id}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-full bg-secondary/30 flex items-center justify-center">
                        {agent.score !== null ? (
                          <span className="text-lg font-display text-primary">{Math.round(agent.score)}</span>
                        ) : (
                          <span className="text-lg text-muted-foreground">--</span>
                        )}
                      </div>
                      <div>
                        <p className="font-medium">{agent.agent_name || 'Unknown Agent'}</p>
                        <p className="text-xs text-muted-foreground">{agent.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <Badge className={getTierColor(agent.tier)}>
                        {getTierLabel(agent.tier)}
                      </Badge>
                      <div className="flex items-center gap-1">
                        {getTrendIcon(agent.trend_direction)}
                      </div>
                      <ChevronRight className="w-4 h-4 text-muted-foreground" />
                    </div>
                  </div>
                  {agent.flags?.length > 0 && (
                    <div className="mt-2 flex gap-2">
                      {agent.flags.map((flag, idx) => (
                        <Badge key={idx} variant="outline" className="text-xs border-red-600/30 text-red-400">
                          {flag}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              
              {overview?.agents?.length === 0 && (
                <div className="text-center py-8 text-muted-foreground">
                  <Shield className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>No agents found</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Agent Details Panel */}
        <Card className="glass-card border-white/10">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5 text-primary" />
              Score Details
            </CardTitle>
          </CardHeader>
          <CardContent>
            {agentDetails ? (
              <div className="space-y-4">
                {/* Overall Score */}
                <div className="text-center p-4 rounded-lg bg-secondary/20">
                  <div className={`text-5xl font-display mb-2 ${
                    agentDetails.score !== null ? 'text-primary' : 'text-muted-foreground'
                  }`}>
                    {agentDetails.score !== null ? Math.round(agentDetails.score) : '--'}
                  </div>
                  <Badge className={getTierColor(agentDetails.tier)}>
                    {getTierLabel(agentDetails.tier)}
                  </Badge>
                  <p className={`text-xs mt-2 ${getConfidenceColor(agentDetails.confidence)}`}>
                    Confidence: {agentDetails.confidence}
                  </p>
                </div>

                {/* Status */}
                <div className="p-3 rounded bg-secondary/10 text-sm">
                  <p className="text-muted-foreground">{agentDetails.summary}</p>
                </div>

                {/* Category Breakdown */}
                {agentDetails.categories?.length > 0 && (
                  <div className="space-y-3">
                    <p className="text-sm font-medium">Category Breakdown</p>
                    {agentDetails.categories.map((cat, idx) => (
                      <div key={idx} className="space-y-1">
                        <div className="flex justify-between text-xs">
                          <span className="text-muted-foreground">
                            {cat.category.replace(/_/g, ' ')}
                          </span>
                          <span className={cat.signal_available ? 'text-foreground' : 'text-muted-foreground'}>
                            {cat.signal_available ? `${Math.round(cat.raw_score)}/100` : 'N/A'}
                          </span>
                        </div>
                        <Progress 
                          value={cat.signal_available ? cat.raw_score : 0} 
                          className="h-2"
                        />
                        <p className="text-xs text-muted-foreground">
                          Weight: {(cat.weight * 100).toFixed(0)}%
                        </p>
                      </div>
                    ))}
                  </div>
                )}

                {/* Flags */}
                {agentDetails.flags?.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-sm font-medium text-red-400">Flags</p>
                    {agentDetails.flags.map((flag, idx) => (
                      <Badge key={idx} variant="outline" className="text-xs border-red-600/30 text-red-400 block text-center py-1">
                        {flag}
                      </Badge>
                    ))}
                  </div>
                )}

                {/* Improvement Suggestions */}
                {agentDetails.improvement_suggestions?.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-sm font-medium text-yellow-400">Improvement Areas</p>
                    <ul className="text-xs text-muted-foreground space-y-1">
                      {agentDetails.improvement_suggestions.map((suggestion, idx) => (
                        <li key={idx}>• {suggestion}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Metadata */}
                <div className="text-xs text-muted-foreground space-y-1 pt-4 border-t border-white/10">
                  <p>Transactions: {agentDetails.total_transactions}</p>
                  <p>Events: {agentDetails.total_events}</p>
                  <p>Calculated: {new Date(agentDetails.calculated_at).toLocaleString()}</p>
                </div>
              </div>
            ) : (
              <div className="text-center py-12 text-muted-foreground">
                <Shield className="w-8 h-8 mx-auto mb-4 opacity-50" />
                <p>Select an agent to view details</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ARSAdminPanel;
